import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Plus, Trash, Eye, PencilSimple, Check, X, Warning,
  CaretDown, CaretUp, ArrowClockwise, FileText, ShieldCheck,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  'Draft':        { bg: 'bg-zinc-100', text: 'text-zinc-700', border: 'border-zinc-300' },
  'Submitted':    { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
  'Under Review': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
  'Approved':     { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' },
  'Rejected':     { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
  'Cancelled':    { bg: 'bg-zinc-50', text: 'text-zinc-500', border: 'border-zinc-200' },
};

export const VariationOrders = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vos, setVos] = useState([]);
  const [trades, setTrades] = useState([]);
  const [project, setProject] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingVo, setEditingVo] = useState(null);
  const [expandedVo, setExpandedVo] = useState(null);
  const [voSummary, setVoSummary] = useState(null);
  const [commercialRisk, setCommercialRisk] = useState(null);

  const role = user?.role;
  const canCreate = ['super_admin', 'admin', 'project_lead', 'project_manager', 'supervisor'].includes(role);
  const canEditRates = ['super_admin', 'admin', 'project_lead'].includes(role);
  const canApprove = ['admin', 'super_admin'].includes(role);
  const canSeeFinancials = role !== 'supervisor';
  const canSeeRisk = ['super_admin', 'admin', 'project_lead'].includes(role);

  const fetchData = useCallback(async () => {
    try {
      const [vosRes, tradesRes, projRes, quotesRes, summaryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/variation-orders?project_id=${projectId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/quote-history`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/vo-summary`, { withCredentials: true }),
      ]);
      setVos(vosRes.data);
      setTrades(tradesRes.data);
      setProject(projRes.data);
      setQuotes(quotesRes.data);
      setVoSummary(summaryRes.data);

      if (canSeeRisk) {
        const riskRes = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/commercial-risk`, { withCredentials: true }).catch(() => ({ data: null }));
        setCommercialRisk(riskRes.data);
      }
    } catch (err) {
      toast.error('Failed to load variation orders');
    } finally {
      setLoading(false);
    }
  }, [projectId, canSeeRisk]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeQuote = quotes.find(q => q.is_locked) || quotes.find(q => q.status === 'Approved' || q.status === 'Confirmed') || (quotes.length > 0 ? quotes[0] : null);

  const handleDelete = async (voId) => {
    if (!window.confirm('Delete this variation order?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/variation-orders/${voId}`, { withCredentials: true });
      toast.success('VO deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleStatusChange = async (voId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/variation-orders/${voId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`VO status updated to ${newStatus}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Status change failed'); }
  };

  const handleApproval = async (voId, action, remarks = '') => {
    try {
      await axios.post(`${BACKEND_URL}/api/variation-orders/${voId}/approval`, { action, remarks }, { withCredentials: true });
      toast.success(`VO ${action}d successfully`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-500">Loading...</div></div>;

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button onClick={() => navigate(`/projects/${projectId}`)} variant="outline" className="border-zinc-300 rounded-none h-8 text-xs" data-testid="vo-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Project Hub
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold tracking-tight text-zinc-900" data-testid="vo-page-title">Variation Orders</h1>
          <p className="text-sm text-zinc-500">{project?.pi_number} — {project?.client_name}</p>
        </div>
        {canCreate && activeQuote && (
          <Button onClick={() => { setEditingVo(null); setShowCreate(true); }} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="create-vo-btn">
            <Plus size={16} className="mr-1" /> New VO
          </Button>
        )}
      </div>

      {/* VO Summary Card */}
      {voSummary && (
        <VOSummaryCard summary={voSummary} canSeeFinancials={canSeeFinancials} />
      )}

      {/* Commercial Risk Warning */}
      {canSeeRisk && commercialRisk && commercialRisk.total_at_risk > 0 && (
        <CommercialRiskBanner risk={commercialRisk} />
      )}

      {/* Create/Edit VO Form */}
      {showCreate && (
        <VOForm
          trades={trades}
          quoteId={activeQuote?.id}
          projectId={projectId}
          editingVo={editingVo}
          role={role}
          canEditRates={canEditRates}
          onClose={() => { setShowCreate(false); setEditingVo(null); }}
          onSaved={() => { setShowCreate(false); setEditingVo(null); fetchData(); }}
        />
      )}

      {/* VO List */}
      {vos.length === 0 && !showCreate ? (
        <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="vo-empty-state">
          <FileText size={48} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-base font-medium text-zinc-600">No Variation Orders yet</p>
          <p className="text-xs text-zinc-400 mt-1">Click "Add Variation Order" to record scope changes</p>
          <p className="text-sm text-zinc-500 mt-1">Create a VO to track scope changes for this project</p>
        </div>
      ) : (
        <div className="space-y-3 mt-4">
          {vos.map((vo) => (
            <VOCard
              key={vo.id}
              vo={vo}
              expanded={expandedVo === vo.id}
              onToggle={() => setExpandedVo(expandedVo === vo.id ? null : vo.id)}
              onEdit={() => { setEditingVo(vo); setShowCreate(true); }}
              onDelete={() => handleDelete(vo.id)}
              onStatusChange={(s) => handleStatusChange(vo.id, s)}
              onApproval={(action, remarks) => handleApproval(vo.id, action, remarks)}
              canEditRates={canEditRates}
              canApprove={canApprove}
              canSeeFinancials={canSeeFinancials}
              role={role}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== VO SUMMARY CARD ========== */
const VOSummaryCard = ({ summary, canSeeFinancials }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="vo-summary-card">
    <div className="bg-white border border-zinc-200 p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Total VOs</p>
      <p className="text-2xl font-bold text-zinc-900 mt-1">{summary.total_vos}</p>
    </div>
    <div className="bg-white border border-green-200 p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-green-600">Approved</p>
      <p className="text-2xl font-bold text-green-700 mt-1">{summary.counts?.Approved || 0}</p>
      {canSeeFinancials && summary.approved_value != null && <p className="text-xs text-green-600 mt-0.5">Rs.{summary.approved_value?.toLocaleString('en-IN')}</p>}
    </div>
    <div className="bg-white border border-amber-200 p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Pending</p>
      <p className="text-2xl font-bold text-amber-700 mt-1">{(summary.counts?.Draft || 0) + (summary.counts?.Submitted || 0) + (summary.counts?.['Under Review'] || 0)}</p>
      {canSeeFinancials && summary.pending_value != null && <p className="text-xs text-amber-600 mt-0.5">Rs.{summary.pending_value?.toLocaleString('en-IN')}</p>}
    </div>
    <div className="bg-white border border-red-200 p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">Rejected</p>
      <p className="text-2xl font-bold text-red-700 mt-1">{summary.counts?.Rejected || 0}</p>
    </div>
    {canSeeFinancials && (
      <div className={`bg-white border p-4 ${summary.at_risk_value > 0 ? 'border-orange-300 bg-orange-50' : 'border-zinc-200'}`}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-orange-600">At Risk</p>
        <p className="text-2xl font-bold text-orange-700 mt-1">Rs.{(summary.at_risk_value || 0).toLocaleString('en-IN')}</p>
      </div>
    )}
  </div>
);

/* ========== COMMERCIAL RISK BANNER ========== */
const CommercialRiskBanner = ({ risk }) => (
  <div className="border-2 border-orange-300 bg-orange-50 p-4 mb-6" data-testid="commercial-risk-banner">
    <div className="flex items-start gap-3">
      <Warning size={22} weight="fill" className="text-orange-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h3 className="text-sm font-bold text-orange-800">Commercial Risk / Unapproved Work</h3>
        <p className="text-xs text-orange-700 mt-1">
          <strong>Rs.{risk.total_at_risk.toLocaleString('en-IN')}</strong> in variation orders not yet commercially approved.
        </p>
        <div className="flex gap-4 mt-2 text-xs">
          {risk.not_submitted_count > 0 && (
            <span className="text-orange-700">
              <Warning size={12} className="inline mr-0.5 -mt-0.5" weight="fill" />
              {risk.not_submitted_count} VO(s) not submitted
            </span>
          )}
          {risk.unapproved_count > 0 && (
            <span className="text-orange-700">
              <ArrowClockwise size={12} className="inline mr-0.5 -mt-0.5" />
              {risk.unapproved_count} VO(s) pending approval
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

/* ========== VO CARD ========== */
const VOCard = ({ vo, expanded, onToggle, onEdit, onDelete, onStatusChange, onApproval, canEditRates, canApprove, canSeeFinancials, role }) => {
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [showApproval, setShowApproval] = useState(false);
  const st = STATUS_STYLES[vo.status] || STATUS_STYLES['Draft'];

  return (
    <div className={`bg-white border ${st.border} transition-all`} data-testid={`vo-card-${vo.id}`}>
      {/* Header Row */}
      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={onToggle} data-testid={`vo-toggle-${vo.id}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-sm font-mono font-bold text-zinc-700" data-testid={`vo-number-${vo.id}`}>{vo.vo_number}</span>
          <span className={`text-xs font-bold px-2 py-0.5 ${st.bg} ${st.text} border ${st.border}`} data-testid={`vo-status-${vo.id}`}>
            {vo.status}
          </span>
          <span className="text-sm font-medium text-zinc-800 truncate">{vo.title}</span>
        </div>
        <div className="flex items-center gap-3">
          {canSeeFinancials && vo.total_amount != null && (
            <span className="text-sm font-bold text-blue-700" data-testid={`vo-total-${vo.id}`}>
              Rs.{vo.total_amount.toLocaleString('en-IN')}
            </span>
          )}
          {expanded ? <CaretUp size={16} className="text-zinc-400" /> : <CaretDown size={16} className="text-zinc-400" />}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-zinc-100 p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
            <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Reason</span><p className="mt-0.5 text-zinc-800">{vo.reason}</p></div>
            <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Created By</span><p className="mt-0.5 text-zinc-800">{vo.created_by_name}</p></div>
            <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Created</span><p className="mt-0.5 text-zinc-800">{new Date(vo.created_at).toLocaleDateString()}</p></div>
            {vo.remarks && <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Remarks</span><p className="mt-0.5 text-zinc-800">{vo.remarks}</p></div>}
          </div>

          {/* VO Line Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid={`vo-line-items-table-${vo.id}`}>
              <thead>
                <tr className="bg-zinc-50 border-y border-zinc-200">
                  <th className="text-left p-2 font-bold uppercase tracking-wider text-zinc-500">#</th>
                  <th className="text-left p-2 font-bold uppercase tracking-wider text-zinc-500">Description</th>
                  <th className="text-left p-2 font-bold uppercase tracking-wider text-zinc-500">Trade</th>
                  <th className="text-right p-2 font-bold uppercase tracking-wider text-zinc-500">Qty</th>
                  <th className="text-left p-2 font-bold uppercase tracking-wider text-zinc-500">Unit</th>
                  {canSeeFinancials && <th className="text-right p-2 font-bold uppercase tracking-wider text-zinc-500">Rate</th>}
                  {canSeeFinancials && <th className="text-right p-2 font-bold uppercase tracking-wider text-zinc-500">Amount</th>}
                </tr>
              </thead>
              <tbody>
                {(vo.line_items || []).map((item, idx) => (
                  <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="p-2 text-zinc-400">{idx + 1}</td>
                    <td className="p-2 text-zinc-800 font-medium">{item.description}</td>
                    <td className="p-2 text-zinc-600">{item.trade_name}</td>
                    <td className="p-2 text-right text-zinc-800">{item.quantity}</td>
                    <td className="p-2 text-zinc-600">{item.unit}</td>
                    {canSeeFinancials && <td className="p-2 text-right text-zinc-800">{item.rate != null ? `Rs.${item.rate.toLocaleString('en-IN')}` : '—'}</td>}
                    {canSeeFinancials && <td className="p-2 text-right font-bold text-zinc-900">{item.amount != null ? `Rs.${item.amount.toLocaleString('en-IN')}` : '—'}</td>}
                  </tr>
                ))}
              </tbody>
              {canSeeFinancials && (
                <tfoot>
                  <tr className="bg-zinc-50 border-t-2 border-zinc-300">
                    <td colSpan={5} className="p-2 text-right font-bold uppercase tracking-wider text-zinc-600">Total</td>
                    <td className="p-2"></td>
                    <td className="p-2 text-right font-bold text-blue-700 text-sm">Rs.{(vo.total_amount || 0).toLocaleString('en-IN')}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Approval History */}
          {vo.approval_history && vo.approval_history.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Approval History</h4>
              <div className="space-y-1">
                {vo.approval_history.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-zinc-600">
                    {a.action === 'approve' ? <Check size={12} className="text-green-600" /> : <X size={12} className="text-red-600" />}
                    <span className="font-medium">{a.user_name}</span>
                    <span>({a.role})</span>
                    <span className="text-zinc-400">—</span>
                    <span className={a.action === 'approve' ? 'text-green-700' : 'text-red-700'}>{a.status}</span>
                    {a.remarks && <span className="text-zinc-400 italic">"{a.remarks}"</span>}
                    <span className="text-zinc-400 ml-auto">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-100 flex-wrap">
            {/* Edit - only Draft/Submitted, only PL/Admin/SA */}
            {canEditRates && ['Draft', 'Submitted'].includes(vo.status) && (
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-zinc-300" onClick={onEdit} data-testid={`vo-edit-${vo.id}`}>
                <PencilSimple size={12} className="mr-1" /> Edit
              </Button>
            )}

            {/* Submit - only Draft with all rates filled */}
            {vo.status === 'Draft' && (
              <Button size="sm" className="rounded-none h-7 text-xs bg-blue-700 hover:bg-blue-800 text-white" onClick={() => onStatusChange('Submitted')} data-testid={`vo-submit-${vo.id}`}>
                Submit for Approval
              </Button>
            )}

            {/* Approve/Reject buttons */}
            {canApprove && ['Submitted', 'Under Review'].includes(vo.status) && (
              <>
                {!showApproval ? (
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-green-300 text-green-700 hover:bg-green-50" onClick={() => setShowApproval(true)} data-testid={`vo-review-${vo.id}`}>
                    <ShieldCheck size={12} className="mr-1" /> Review
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={approvalRemarks}
                      onChange={(e) => setApprovalRemarks(e.target.value)}
                      placeholder="Remarks (optional)"
                      className="rounded-none h-7 text-xs flex-1 max-w-xs"
                      data-testid={`vo-approval-remarks-${vo.id}`}
                    />
                    <Button size="sm" className="rounded-none h-7 text-xs bg-green-700 hover:bg-green-800 text-white" onClick={() => { onApproval('approve', approvalRemarks); setShowApproval(false); setApprovalRemarks(''); }} data-testid={`vo-approve-${vo.id}`}>
                      <Check size={12} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" className="rounded-none h-7 text-xs bg-red-700 hover:bg-red-800 text-white" onClick={() => { onApproval('reject', approvalRemarks); setShowApproval(false); setApprovalRemarks(''); }} data-testid={`vo-reject-${vo.id}`}>
                      <X size={12} className="mr-1" /> Reject
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setShowApproval(false)}>Cancel</Button>
                  </div>
                )}
              </>
            )}

            {/* Re-open rejected */}
            {vo.status === 'Rejected' && canEditRates && (
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => onStatusChange('Draft')} data-testid={`vo-reopen-${vo.id}`}>
                <ArrowClockwise size={12} className="mr-1" /> Re-open as Draft
              </Button>
            )}

            {/* Cancel */}
            {['Draft', 'Submitted'].includes(vo.status) && canEditRates && (
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-zinc-300 text-zinc-500 hover:bg-zinc-100" onClick={() => onStatusChange('Cancelled')} data-testid={`vo-cancel-${vo.id}`}>
                Cancel VO
              </Button>
            )}

            {/* Delete - only Draft/Cancelled, admin/SA */}
            {['Draft', 'Cancelled'].includes(vo.status) && ['admin', 'super_admin'].includes(role) && (
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-red-300 text-red-700 hover:bg-red-50 ml-auto" onClick={onDelete} data-testid={`vo-delete-${vo.id}`}>
                <Trash size={12} className="mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ========== VO CREATE/EDIT FORM ========== */
const VOForm = ({ trades, quoteId, projectId, editingVo, role, canEditRates, onClose, onSaved }) => {
  const [title, setTitle] = useState(editingVo?.title || '');
  const [reason, setReason] = useState(editingVo?.reason || '');
  const [remarks, setRemarks] = useState(editingVo?.remarks || '');
  const [lineItems, setLineItems] = useState(
    editingVo?.line_items?.length
      ? editingVo.line_items.map(li => ({
          description: li.description || '',
          trade_id: li.trade_id || '',
          quantity: li.quantity || '',
          unit: li.unit || '',
          rate: li.rate || '',
          specification: li.specification || '',
          make: li.make || '',
        }))
      : [{ description: '', trade_id: '', quantity: '', unit: '', rate: '', specification: '', make: '' }]
  );
  const [saving, setSaving] = useState(false);

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', trade_id: '', quantity: '', unit: '', rate: '', specification: '', make: '' }]);
  };

  const removeLineItem = (idx) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== idx));
  };

  const updateLineItem = (idx, field, value) => {
    const updated = [...lineItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setLineItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (lineItems.some(li => !li.description.trim() || !li.trade_id || !li.quantity || !li.unit)) {
      toast.error('All line items must have description, trade, quantity, and unit');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title,
        reason,
        remarks,
        line_items: lineItems.map(li => ({
          description: li.description,
          trade_id: li.trade_id,
          quantity: parseFloat(li.quantity) || 0,
          unit: li.unit,
          rate: li.rate ? parseFloat(li.rate) : null,
          specification: li.specification,
          make: li.make,
        })),
      };

      if (editingVo) {
        await axios.put(`${BACKEND_URL}/api/variation-orders/${editingVo.id}`, payload, { withCredentials: true });
        toast.success('VO updated');
      } else {
        payload.project_id = projectId;
        payload.quote_id = quoteId;
        await axios.post(`${BACKEND_URL}/api/variation-orders`, payload, { withCredentials: true });
        toast.success('VO created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save VO');
    } finally { setSaving(false); }
  };

  const isSupervisor = role === 'supervisor';

  return (
    <div className="bg-white border-2 border-blue-300 p-6 mb-6" data-testid="vo-form">
      <h3 className="text-sm font-bold text-zinc-900 mb-4">{editingVo ? 'Edit Variation Order' : 'New Variation Order'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">VO Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none mt-1" placeholder="e.g., Additional false ceiling work" data-testid="vo-title-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Reason *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none mt-1" placeholder="Client requested scope change" data-testid="vo-reason-input" />
          </div>
        </div>
        <div className="mb-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Remarks</Label>
          <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm mt-1" placeholder="Additional notes..." data-testid="vo-remarks-input" />
        </div>

        {/* Line Items Table */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Line Items *</Label>
            <Button type="button" size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={addLineItem} data-testid="vo-add-line-item">
              <Plus size={12} className="mr-1" /> Add Item
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse border border-zinc-200">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="p-2 text-left font-bold uppercase tracking-wider text-zinc-500 border-b">#</th>
                  <th className="p-2 text-left font-bold uppercase tracking-wider text-zinc-500 border-b min-w-[200px]">Description</th>
                  <th className="p-2 text-left font-bold uppercase tracking-wider text-zinc-500 border-b min-w-[140px]">Trade</th>
                  <th className="p-2 text-left font-bold uppercase tracking-wider text-zinc-500 border-b w-20">Qty</th>
                  <th className="p-2 text-left font-bold uppercase tracking-wider text-zinc-500 border-b w-20">Unit</th>
                  {!isSupervisor && <th className="p-2 text-left font-bold uppercase tracking-wider text-zinc-500 border-b w-24">Rate</th>}
                  {!isSupervisor && <th className="p-2 text-left font-bold uppercase tracking-wider text-zinc-500 border-b w-24">Amount</th>}
                  <th className="p-2 border-b w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => {
                  const amt = (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 0);
                  return (
                    <tr key={idx} className="border-b border-zinc-100">
                      <td className="p-1.5 text-zinc-400">{idx + 1}</td>
                      <td className="p-1.5">
                        <Input value={li.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                          className="rounded-none h-7 text-xs" placeholder="Description" data-testid={`vo-item-desc-${idx}`} />
                      </td>
                      <td className="p-1.5">
                        <select value={li.trade_id} onChange={(e) => updateLineItem(idx, 'trade_id', e.target.value)}
                          className="w-full h-7 text-xs border border-zinc-200 bg-white px-1" data-testid={`vo-item-trade-${idx}`}>
                          <option value="">Select Trade</option>
                          {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </td>
                      <td className="p-1.5">
                        <Input type="number" step="0.01" value={li.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                          className="rounded-none h-7 text-xs" placeholder="0" data-testid={`vo-item-qty-${idx}`} />
                      </td>
                      <td className="p-1.5">
                        <Input value={li.unit} onChange={(e) => updateLineItem(idx, 'unit', e.target.value)}
                          className="rounded-none h-7 text-xs" placeholder="Sqft" data-testid={`vo-item-unit-${idx}`} />
                      </td>
                      {!isSupervisor && (
                        <td className="p-1.5">
                          <Input type="number" step="0.01" value={li.rate} onChange={(e) => updateLineItem(idx, 'rate', e.target.value)}
                            className="rounded-none h-7 text-xs" placeholder="0" disabled={!canEditRates} data-testid={`vo-item-rate-${idx}`} />
                        </td>
                      )}
                      {!isSupervisor && (
                        <td className="p-1.5 text-right font-medium text-zinc-700">
                          {amt > 0 ? `Rs.${amt.toLocaleString('en-IN')}` : '—'}
                        </td>
                      )}
                      <td className="p-1.5 text-center">
                        {lineItems.length > 1 && (
                          <button type="button" onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-600">
                            <Trash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-2">
          <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="vo-save-btn">
            {saving ? 'Saving...' : (editingVo ? 'Update VO' : 'Create VO')}
          </Button>
          <Button type="button" variant="outline" className="rounded-none border-zinc-300" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};

export default VariationOrders;
